// @givmo/compliance — what came back after a send, on the send page (CCM-R1-RESULTS-WEB; D-CCM-68,
// REVIEW-30's frames a1–d3): the list "Delivery reports and answers", the act that says the email
// service's report is wrong on a "Reported as sent" card, and the two dialogs: recording the
// agency's answer (c1–c3) and saying a report or a record is wrong (d1, d2).
//
// Presentational: the page hands each its rows, its words and its acts. Every line says where it
// came from: the email service's reports name no person, a person's record names who recorded it
// and is dated by the day they did, and nothing reads as accepted until someone attaches the
// confirmation ("It accepted it" opens that act, never a record).

import { useState, type FormEvent } from 'react';
import {
  Button,
  Dialog,
  ErrorMessage,
  FOCUS_RING,
  RadioTile,
  RadioTileGroup,
  Textarea,
  cn,
  testIdProp,
} from '@givmo/design-system';
import type {
  ComplianceSubmissionEvidenceAnswer,
  ComplianceSubmissionEvidenceRecord,
} from '@givmo/api-client';
import { useArtifactDownload } from './artifact-download';
import { ComplianceDateField } from './compliance-date-field';
import { serverMessage, type ComplianceArtifact } from './compliance-decode';
import {
  ANSWERED,
  ATTACH_THE_CONFIRMATION,
  DELIVERY_REPORTS_AND_ANSWERS,
  GIVMOS_EMAIL_SERVICE,
  IT_ACCEPTED,
  IT_ACKNOWLEDGED,
  IT_ASKED_FOR_CHANGES,
  IT_REJECTED,
  KEEP_YOUR_RECORD,
  NOTE_OPTIONAL,
  OWN_ACCOUNT_HELPER,
  RECORD_IT,
  RECORD_THAT_IT_DIDNT_ARRIVE,
  THIS_ISNT_RIGHT,
  THIS_ISNT_RIGHT_TITLE,
  WHATS_WRONG,
  WHAT_YOU_FOUND,
  WHICH_SEND,
  acceptedNeedsConfirmationLine,
  answerDateLabel,
  answerRowLine,
  answerUnderLine,
  attachMessageLabel,
  attachMessageOptional,
  itDidntReachIntro,
  itDidntReachTitle,
  keepTheirRecordLine,
  recordAnswerIntro,
  recordAnswerLabel,
  servedDay,
  whatItSaidLabel,
  type DeliveryListRow,
  type SendOption,
} from './compliance-results-model';
import { noteLine, whenOf } from './compliance-signing-model';
import type { RecordedAgencyAnswer, SubmissionEvidence } from './compliance-submission-decode';
import { CANCEL } from './compliance-submission-model';
import { AttachSlot } from './filing-panel-forms';

const PREFIX = 'compliance-send';

/** The line a failed act says when the server gave none (the module's own). */
const TRY_AGAIN_LINE = 'Please try again.';

/** A choice's heading in a dialog, as the handoff form heads its own (REVIEW-30's c1). */
const GROUP_LABEL_CLASS = 'text-s13 font-w600 text-text-strong';

/** The server's limit on a note (`ComplianceSubmissionEvidenceRecord.note`, `…Answer.note`). */
const NOTE_LIMIT = 500;

// ── The acts ───────────────────────────────────────────────────────────────────────────────────

/** One act in "What happens next" or on a "Reported as sent" card: the step's own act leads. */
export function ResultsAct({
  label,
  primary = false,
  onClick,
  testId,
}: {
  label: string;
  primary?: boolean;
  onClick: () => void;
  testId: string;
}) {
  return (
    <Button
      appearance="saas"
      tone={primary ? 'primary' : 'secondary'}
      size="sm"
      onClick={onClick}
      testId={testId}
    >
      {label}
    </Button>
  );
}

// ── The list (section 1) ───────────────────────────────────────────────────────────────────────

/** A document's own name, which downloads it (as a document's name does on a filing's panel). */
function DocumentName({ artifactId, name }: { artifactId: string; name: string }) {
  const download = useArtifactDownload(artifactId);
  return (
    <button
      type="button"
      onClick={() => download.mutate()}
      className={cn(
        'rounded-xs text-left underline underline-offset-2 hover:text-text-secondary',
        FOCUS_RING,
      )}
      {...testIdProp(`${PREFIX}-evidence-document-${artifactId}`)}
    >
      {name}
    </button>
  );
}

/** A line with the agency's own day in it kept on one line, as the frames keep it (a7). */
function keepDayWhole(line: string, day: string) {
  const at = line.lastIndexOf(day);
  if (at < 0) return line;
  return (
    <>
      {line.slice(0, at)}
      <span className="whitespace-nowrap">{day}</span>
      {line.slice(at + day.length)}
    </>
  );
}

/** " · {its document}" after a record, where the organization's documents name it. */
function withDocument(
  artifactId: string | undefined,
  artifactName: (artifactId: string) => string | undefined,
) {
  const name = artifactId !== undefined ? artifactName(artifactId) : undefined;
  return artifactId !== undefined && name !== undefined ? (
    <>
      {' · '}
      <DocumentName artifactId={artifactId} name={name} />
    </>
  ) : null;
}

/**
 * "Delivery reports and answers": the shown send's reports and records, oldest first, each with
 * where it came from. An answered row stays, greyed and marked Answered, with the answer under it
 * (d3); a person's record carries This isn't right for whoever may say so (section 4).
 */
export function DeliveryList({
  rows,
  jurisdiction,
  zone,
  nameOf,
  artifactName,
  canAnswer,
  onAnswer,
}: {
  rows: readonly DeliveryListRow[];
  jurisdiction: string;
  zone: string | undefined;
  nameOf: (userId: string | undefined) => string | undefined;
  /** A document's own name, from the organization's documents; unnamed, no link is drawn. */
  artifactName: (artifactId: string) => string | undefined;
  /** Whether a person's record offers This isn't right (a Compliance Owner or a Global Admin). */
  canAnswer: boolean;
  onAnswer: (row: SubmissionEvidence) => void;
}) {
  if (rows.length === 0) return null;
  return (
    <div
      className="flex flex-col gap-8 border-t border-border-subtle pt-14"
      {...testIdProp(`${PREFIX}-delivery-list`)}
    >
      <p className="text-s14 font-w600 text-text-strong">{DELIVERY_REPORTS_AND_ANSWERS}</p>
      <ul className="flex flex-col gap-10">
        {rows.map((entry) => {
          const { row, answeredBy } = entry;
          const answered = answeredBy !== undefined;
          const recorder = nameOf(row.recordedByUserId);
          const when = whenOf(row.occurredAt, zone);
          return (
            <li
              key={row.evidenceId}
              className="flex flex-col gap-4"
              {...testIdProp(`${PREFIX}-evidence-${row.evidenceId}`)}
            >
              <p
                className={cn(
                  'text-s13 leading-h15',
                  answered ? 'text-text-tertiary' : 'text-text',
                )}
                {...testIdProp(`${PREFIX}-evidence-line`)}
              >
                {entry.report !== undefined ? (
                  <>
                    <span className="tabular-nums">{`${when.day} at ${when.time}`}</span>
                    {' · '}
                    {entry.report}
                    {` · ${GIVMOS_EMAIL_SERVICE}`}
                    {answered ? ` · ${ANSWERED}` : ''}
                  </>
                ) : (
                  <>
                    <span className="tabular-nums">
                      {servedDay(row.recordedOn, row.recordedAt, zone)}
                    </span>
                    {' · '}
                    {keepDayWhole(
                      answerRowLine(
                        entry.answer as RecordedAgencyAnswer,
                        recorder,
                        jurisdiction,
                        servedDay(row.occurredOn, row.occurredAt, zone),
                      ),
                      servedDay(row.occurredOn, row.occurredAt, zone),
                    )}
                    {withDocument(row.artifactId, artifactName)}
                    {answered ? ` · ${ANSWERED}` : ''}
                  </>
                )}
              </p>
              {entry.answer !== undefined && row.note !== undefined && row.note.length > 0 && (
                <p
                  className="break-words text-s13 leading-h15 text-text-secondary"
                  {...testIdProp(`${PREFIX}-evidence-note`)}
                >
                  {noteLine(recorder, row.note)}
                </p>
              )}
              {answeredBy !== undefined && (
                <p
                  className="break-words text-s13 leading-h15 text-text-secondary"
                  {...testIdProp(`${PREFIX}-evidence-answer`)}
                >
                  {answerUnderLine(
                    entry.report !== undefined,
                    nameOf(answeredBy.recordedByUserId),
                    servedDay(answeredBy.recordedOn, answeredBy.recordedAt, zone),
                    jurisdiction,
                    answeredBy.note ?? '',
                  )}
                  {withDocument(answeredBy.artifactId, artifactName)}
                </p>
              )}
              {entry.answer !== undefined && !answered && canAnswer && (
                <div>
                  <ResultsAct
                    label={THIS_ISNT_RIGHT}
                    onClick={() => onAnswer(row)}
                    testId={`${PREFIX}-not-right-${row.evidenceId}`}
                  />
                </div>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}

// ── Recording the agency's answer (section 3, c1–c3) ───────────────────────────────────────────

type Said = RecordedAgencyAnswer | 'accepted';

const SAID: ReadonlyArray<{ value: Said; label: string }> = [
  { value: 'agency_acknowledgment', label: IT_ACKNOWLEDGED },
  { value: 'agency_rejection', label: IT_REJECTED },
  { value: 'agency_correction_request', label: IT_ASKED_FOR_CHANGES },
  { value: 'accepted', label: IT_ACCEPTED },
];

function RecordAnswerForm({
  jurisdiction,
  sends,
  today,
  pending,
  failure,
  onRecord,
  onAttachConfirmation,
  onClose,
}: {
  jurisdiction: string;
  sends: readonly SendOption[];
  today: string | undefined;
  pending: boolean;
  failure: unknown;
  onRecord: (body: ComplianceSubmissionEvidenceRecord) => void;
  onAttachConfirmation: () => void;
  onClose: () => void;
}) {
  // Nothing is chosen for the person (REVIEW-30 section 3).
  const [sendKey, setSendKey] = useState('');
  const [said, setSaid] = useState<Said | ''>('');
  const [occurredOn, setOccurredOn] = useState('');
  const [message, setMessage] = useState<ComplianceArtifact | undefined>(undefined);
  const [note, setNote] = useState('');
  const chosen = sends.find((option) => option.key === sendKey);
  const accepted = said === 'accepted';
  const ready = accepted || (chosen !== undefined && said !== '' && occurredOn !== '');

  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (!ready || pending) return;
    // "It accepted it" records nothing: it opens the confirmation's own act.
    if (said === 'accepted') {
      onAttachConfirmation();
      return;
    }
    if (chosen === undefined) return;
    const trimmed = note.trim();
    onRecord({
      ...('attemptId' in chosen.send
        ? { attempt_id: chosen.send.attemptId }
        : { handoff_id: chosen.send.handoffId }),
      kind: said,
      // With the agency's message attached, it is the agency's own; without it, the
      // organization's own account.
      source: message !== undefined ? 'agency_message' : 'organization_upload',
      occurred_on: occurredOn,
      ...(message !== undefined ? { artifact_id: message.artifactId } : {}),
      ...(trimmed.length > 0 ? { note: trimmed } : {}),
    });
  };

  return (
    <form onSubmit={submit} className="flex flex-col gap-16">
      <Dialog.Title className="font-sans text-s18 font-w700 tracking-givmo text-text-strong">
        {recordAnswerLabel(jurisdiction)}
      </Dialog.Title>
      <Dialog.Description className="font-sans text-s14 font-w400 leading-h15 tracking-givmo text-text-secondary">
        {recordAnswerIntro(jurisdiction)}
      </Dialog.Description>
      <div className="flex flex-col gap-8">
        <p className={GROUP_LABEL_CLASS}>{WHICH_SEND}</p>
        <RadioTileGroup
          value={sendKey}
          onValueChange={setSendKey}
          aria-label={WHICH_SEND}
          testId={`${PREFIX}-answer-send`}
        >
          {sends.map((option) => (
            <RadioTile
              key={option.key}
              value={option.key}
              label={option.label}
              testId={`${PREFIX}-answer-send-${option.key}`}
            />
          ))}
        </RadioTileGroup>
      </div>
      <div className="flex flex-col gap-8">
        <p className={GROUP_LABEL_CLASS}>{whatItSaidLabel(jurisdiction)}</p>
        <RadioTileGroup
          value={said}
          onValueChange={(next) => setSaid(next as Said)}
          aria-label={whatItSaidLabel(jurisdiction)}
          testId={`${PREFIX}-answer-said`}
        >
          {SAID.map((option) => (
            <RadioTile
              key={option.value}
              value={option.value}
              label={option.label}
              testId={`${PREFIX}-answer-said-${option.value}`}
            />
          ))}
        </RadioTileGroup>
      </div>
      {accepted ? (
        <p
          className="text-s13 leading-h15 text-text"
          {...testIdProp(`${PREFIX}-answer-accepted-line`)}
        >
          {acceptedNeedsConfirmationLine(jurisdiction)}
        </p>
      ) : (
        <>
          <ComplianceDateField
            label={answerDateLabel(jurisdiction)}
            value={occurredOn}
            onChange={setOccurredOn}
            testId={`${PREFIX}-answer-date`}
            {...(today !== undefined ? { latest: today } : {})}
          />
          <div className="flex flex-col">
            <AttachSlot
              button={attachMessageOptional(jurisdiction)}
              label={attachMessageLabel(jurisdiction)}
              hint={OWN_ACCOUNT_HELPER}
              artifact={message}
              onChange={setMessage}
              testId={`${PREFIX}-answer-message`}
            />
          </div>
          <Textarea
            label={NOTE_OPTIONAL}
            value={note}
            maxLength={NOTE_LIMIT}
            rows={2}
            onChange={(event) => setNote(event.target.value)}
            testId={`${PREFIX}-answer-note`}
          />
        </>
      )}
      {failure !== null && failure !== undefined && (
        <ErrorMessage testId={`${PREFIX}-answer-failure`}>
          {serverMessage(failure, TRY_AGAIN_LINE)}
        </ErrorMessage>
      )}
      <div className="flex flex-wrap gap-8">
        <Button
          appearance="saas"
          tone="primary"
          size="md"
          type="submit"
          disabled={!ready}
          loading={pending}
          testId={`${PREFIX}-answer-record`}
        >
          {accepted ? ATTACH_THE_CONFIRMATION : RECORD_IT}
        </Button>
        <Button
          appearance="saas"
          tone="ghost"
          size="md"
          type="button"
          disabled={pending}
          onClick={onClose}
          testId={`${PREFIX}-answer-cancel`}
        >
          {CANCEL}
        </Button>
      </div>
    </form>
  );
}

/**
 * Record {jurisdiction}'s answer: what the agency told the organization about one send that went
 * out, in the recorder's name. It never records acceptance: "It accepted it" turns the button into
 * Attach the confirmation, which opens that act (D-CCM-68). A refusal is shown as served.
 */
export function RecordAnswerDialog({
  open,
  ...form
}: {
  open: boolean;
  jurisdiction: string;
  sends: readonly SendOption[];
  /** The latest day the date field offers: today (an answer is never after it). */
  today: string | undefined;
  pending: boolean;
  failure: unknown;
  onRecord: (body: ComplianceSubmissionEvidenceRecord) => void;
  onAttachConfirmation: () => void;
  onClose: () => void;
}) {
  return (
    <Dialog.Root
      open={open}
      onOpenChange={(next) => {
        if (!next && !form.pending) form.onClose();
      }}
    >
      {open && (
        <Dialog.Content width={520} testId={`${PREFIX}-record-answer`}>
          <RecordAnswerForm {...form} />
        </Dialog.Content>
      )}
    </Dialog.Root>
  );
}

// ── Saying a report or a record is wrong (section 4, d1 and d2) ────────────────────────────────

/**
 * What an answer says is wrong: the email service's reports that a "Reported as sent" card counts
 * (d1; each is answered, so nothing counts the send as taken any more), or a person's record (d2).
 */
export type AnswerTarget =
  | { kind: 'report'; rows: readonly SubmissionEvidence[] }
  | { kind: 'record'; row: SubmissionEvidence; recorder: string | undefined; own: boolean };

function AnswerForm({
  target,
  jurisdiction,
  pending,
  failure,
  onAnswer,
  onClose,
}: {
  target: AnswerTarget;
  jurisdiction: string;
  pending: boolean;
  failure: unknown;
  onAnswer: (body: ComplianceSubmissionEvidenceAnswer) => void;
  onClose: () => void;
}) {
  const [note, setNote] = useState('');
  const [message, setMessage] = useState<ComplianceArtifact | undefined>(undefined);
  const onReport = target.kind === 'report';
  const trimmed = note.trim();
  const ready = trimmed.length > 0;
  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (!ready || pending) return;
    onAnswer({
      note: trimmed,
      ...(onReport && message !== undefined ? { artifact_id: message.artifactId } : {}),
    });
  };
  return (
    <form onSubmit={submit} className="flex flex-col gap-16">
      <Dialog.Title className="font-sans text-s18 font-w700 tracking-givmo text-text-strong">
        {onReport ? itDidntReachTitle(jurisdiction) : THIS_ISNT_RIGHT_TITLE}
      </Dialog.Title>
      <Dialog.Description className="font-sans text-s14 font-w400 leading-h15 tracking-givmo text-text-secondary">
        {target.kind === 'report'
          ? itDidntReachIntro(jurisdiction)
          : target.own
            ? KEEP_YOUR_RECORD
            : keepTheirRecordLine(target.recorder)}
      </Dialog.Description>
      <Textarea
        label={onReport ? WHAT_YOU_FOUND : WHATS_WRONG}
        value={note}
        maxLength={NOTE_LIMIT}
        rows={3}
        required
        onChange={(event) => setNote(event.target.value)}
        testId={`${PREFIX}-wrong-note`}
      />
      {onReport && (
        <div className="flex flex-col">
          <AttachSlot
            button={attachMessageOptional(jurisdiction)}
            label={attachMessageLabel(jurisdiction)}
            artifact={message}
            onChange={setMessage}
            testId={`${PREFIX}-wrong-message`}
          />
        </div>
      )}
      {failure !== null && failure !== undefined && (
        <ErrorMessage testId={`${PREFIX}-wrong-failure`}>
          {serverMessage(failure, TRY_AGAIN_LINE)}
        </ErrorMessage>
      )}
      <div className="flex flex-wrap gap-8">
        <Button
          appearance="saas"
          tone="primary"
          size="md"
          type="submit"
          disabled={!ready}
          loading={pending}
          testId={`${PREFIX}-wrong-record`}
        >
          {onReport ? RECORD_THAT_IT_DIDNT_ARRIVE : RECORD_IT}
        </Button>
        <Button
          appearance="saas"
          tone="ghost"
          size="md"
          type="button"
          disabled={pending}
          onClick={onClose}
          testId={`${PREFIX}-wrong-cancel`}
        >
          {CANCEL}
        </Button>
      </div>
    </form>
  );
}

/** It didn't reach {jurisdiction}? (d1) and This isn't right? (d2): the record stays, the answer beside it. */
export function AnswerDialog({
  target,
  ...form
}: {
  target: AnswerTarget | undefined;
  jurisdiction: string;
  pending: boolean;
  failure: unknown;
  onAnswer: (body: ComplianceSubmissionEvidenceAnswer) => void;
  onClose: () => void;
}) {
  return (
    <Dialog.Root
      open={target !== undefined}
      onOpenChange={(next) => {
        if (!next && !form.pending) form.onClose();
      }}
    >
      {target !== undefined && (
        <Dialog.Content width={480} testId={`${PREFIX}-wrong`}>
          <AnswerForm target={target} {...form} />
        </Dialog.Content>
      )}
    </Dialog.Root>
  );
}
